/**
 * Video Filtering Utilities
 * Client-side filtering with SV→EN fallback logic (matching backend behavior)
 */

import { Language } from '../../../../pure-js/types/ExpertVideo'

/**
 * Filter videos to only those that are active/published.
 * Only items with isActive === true are shown on the frontend.
 *
 * @param videos - Array of videos to filter
 * @returns Filtered videos
 */
export function filterVideosByActive<T extends { isActive?: boolean }>(videos: T[]): T[] {
  return videos.filter((v) => v.isActive === true)
}

/**
 * Base interface for videos with language and grouping
 */
interface VideoWithLanguage {
  language: Language
  videoGroupId: string
  dateAdded: string
  priority?: number
}

/**
 * Filter videos by language with SV→EN fallback logic
 * Matches the backend filtering logic in ExpertVideoHandler and TutorialVideoHandler
 *
 * @param videos - Array of videos to filter
 * @param language - Language to filter by ('en', 'sv', or undefined for all)
 * @returns Filtered and sorted videos
 */
export function filterVideosByLanguage<T extends VideoWithLanguage>(videos: T[], language?: Language): T[] {
  if (!language) {
    // Return all videos, sorted by priority (if available), then by dateAdded (descending)
    return [...videos].sort((a, b) => {
      // First, sort by priority (if available) - lower numbers = higher priority
      if (a.priority !== undefined && b.priority !== undefined) {
        const priorityComparison = a.priority - b.priority
        if (priorityComparison !== 0) {
          return priorityComparison
        }
      } else if (a.priority !== undefined) {
        return -1 // a has priority, comes first
      } else if (b.priority !== undefined) {
        return 1 // b has priority, comes first
      }
      // If no priority or same priority, sort by dateAdded (descending)
      const dateA = new Date(a.dateAdded).getTime()
      const dateB = new Date(b.dateAdded).getTime()
      return dateB - dateA // Descending order
    })
  }

  if (language === 'sv') {
    // SV→EN fallback logic (matches backend)
    const videoMap = new Map<string, T>()

    // First, add all SV videos (preferred)
    videos
      .filter((v) => v.language === 'sv')
      .forEach((video) => {
        videoMap.set(video.videoGroupId, video)
      })

    // Then, add EN videos as fallback for videoGroupIds without SV
    videos
      .filter((v) => v.language === 'en')
      .forEach((video) => {
        if (!videoMap.has(video.videoGroupId)) {
          videoMap.set(video.videoGroupId, video)
        }
      })

    // Convert to array and sort by priority (if available), then by dateAdded (descending)
    return Array.from(videoMap.values()).sort((a, b) => {
      // First, sort by priority (if available) - lower numbers = higher priority
      if (a.priority !== undefined && b.priority !== undefined) {
        const priorityComparison = a.priority - b.priority
        if (priorityComparison !== 0) {
          return priorityComparison
        }
      } else if (a.priority !== undefined) {
        return -1 // a has priority, comes first
      } else if (b.priority !== undefined) {
        return 1 // b has priority, comes first
      }
      // If no priority or same priority, sort by dateAdded (descending)
      const dateA = new Date(a.dateAdded).getTime()
      const dateB = new Date(b.dateAdded).getTime()
      return dateB - dateA // Descending order
    })
  }

  // For 'en', return only EN videos, sorted by priority (if available), then by dateAdded (descending)
  if (language === 'en') {
    return videos
      .filter((v) => v.language === 'en')
      .sort((a, b) => {
        // First, sort by priority (if available) - lower numbers = higher priority
        if (a.priority !== undefined && b.priority !== undefined) {
          const priorityComparison = a.priority - b.priority
          if (priorityComparison !== 0) {
            return priorityComparison
          }
        } else if (a.priority !== undefined) {
          return -1 // a has priority, comes first
        } else if (b.priority !== undefined) {
          return 1 // b has priority, comes first
        }
        // If no priority or same priority, sort by dateAdded (descending)
        const dateA = new Date(a.dateAdded).getTime()
        const dateB = new Date(b.dateAdded).getTime()
        return dateB - dateA // Descending order
      })
  }

  // Fallback: return all videos sorted by priority (if available), then by dateAdded (descending)
  return [...videos].sort((a, b) => {
    // First, sort by priority (if available) - lower numbers = higher priority
    if (a.priority !== undefined && b.priority !== undefined) {
      const priorityComparison = a.priority - b.priority
      if (priorityComparison !== 0) {
        return priorityComparison
      }
    } else if (a.priority !== undefined) {
      return -1 // a has priority, comes first
    } else if (b.priority !== undefined) {
      return 1 // b has priority, comes first
    }
    // If no priority or same priority, sort by dateAdded (descending)
    const dateA = new Date(a.dateAdded).getTime()
    const dateB = new Date(b.dateAdded).getTime()
    return dateB - dateA // Descending order
  })
}
